/**
 * FileReviewRegister.jsx: BM-H632 (26 Sep 2026).
 *
 * The File Review Register, and where a review is started. Reads
 * GET /api/compliance/file-reviews; starts with POST /api/compliance/file-reviews.
 * The server decides who sees what: the MLCO and COLP see every review, others
 * see the reviews they carried out and the reviews of their own matters.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ClipboardCheck, Loader2, Search } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
// BM-H638: sampling, for the MLCO and COLP.
import FileReviewSampling from "./FileReviewSampling";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

const STATUS_LABEL = { draft: "Draft", submitted: "Open actions", closed: "Closed" };

function day(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function labelOf(list, value) {
  return (list || []).find((o) => o.value === value)?.label || value || "—";
}

export default function FileReviewRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [start, setStart] = useState({ matter_ref: "", review_type: "random_sampled", review_mode: "supervision" });
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const [role, setRole] = useState({});

  useEffect(() => {
    axios.get(`${API}/user-role`, { headers: authHeaders() })
      .then((r) => setRole(r.data || {}))
      .catch(() => setRole({}));
  }, []);

  useEffect(() => {
    axios.get(`${API}/compliance/file-reviews/form`, { headers: authHeaders() })
      .then((r) => setForm(r.data))
      .catch((err) => setError(errorMessage(err, "Could not load the file review form")));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const r = await axios.get(`${API}/compliance/file-reviews?${params.toString()}`, { headers: authHeaders() });
      setData(r.data);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the register"));
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const onStart = async (e) => {
    e.preventDefault();
    if (!start.matter_ref.trim()) {
      setStartError("Enter the matter reference.");
      return;
    }
    setStarting(true);
    setStartError("");
    try {
      const r = await axios.post(`${API}/compliance/file-reviews`, start, { headers: authHeaders() });
      navigate(`/compliance/file-reviews/${r.data.id}`);
    } catch (err) {
      setStartError(errorMessage(err, "Could not start the review"));
    } finally {
      setStarting(false);
    }
  };

  const items = data?.items || [];
  const ratingOptions = (form?.sections || []).flatMap((s) => s.questions).find((qq) => qq.id === "q30")?.options;
  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="file-review-register">
      <div className="flex items-center gap-2">
        <ClipboardCheck size={18} className="text-emerald-300" />
        <h1 className="text-base font-semibold text-slate-100">File reviews</h1>
      </div>

      {(role.is_colp || role.is_mlco) && <FileReviewSampling isColp={!!role.is_colp} onRun={load} />}

      {error && (
        <div className="p-3 rounded-md bg-red-950/40 border border-red-800/50 text-red-200 text-sm" data-testid="file-review-register-error">
          {error}
        </div>
      )}

      <form onSubmit={onStart} className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2" data-testid="file-review-start">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Start a review</div>
        <div className="flex flex-wrap gap-2">
          <input
            value={start.matter_ref}
            onChange={(e) => setStart({ ...start, matter_ref: e.target.value })}
            placeholder="Matter reference"
            className="px-3 py-1.5 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200 placeholder-slate-500"
            data-testid="file-review-start-ref"
          />
          <select
            value={start.review_type}
            onChange={(e) => setStart({ ...start, review_type: e.target.value })}
            className="px-3 py-1.5 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200"
            data-testid="file-review-start-type"
          >
            {(form?.review_types || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={start.review_mode}
            onChange={(e) => setStart({ ...start, review_mode: e.target.value })}
            className="px-3 py-1.5 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200"
            data-testid="file-review-start-mode"
          >
            {(form?.review_modes || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            type="submit"
            disabled={starting || !form}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-700/30 text-emerald-200 border border-emerald-700/60 hover:bg-emerald-700/50 disabled:opacity-50"
            data-testid="file-review-start-btn"
          >
            {starting ? "Starting…" : "Start review"}
          </button>
        </div>
        {startError && <div className="text-xs text-red-300" data-testid="file-review-start-error">{startError}</div>}
      </form>

      <div className="flex flex-wrap gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-1.5 rounded-md text-xs bg-slate-900/60 border border-slate-700/60 text-slate-200"
          data-testid="file-review-filter-status"
        >
          <option value="">Any status</option>
          <option value="draft">Draft</option>
          <option value="submitted">Open actions</option>
          <option value="closed">Closed</option>
        </select>
        <div className="relative flex-1 min-w-[12rem]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search matter, client or name…"
            className="w-full pl-7 pr-3 py-1.5 rounded-md text-xs bg-slate-900/60 border border-slate-700/60 text-slate-200 placeholder-slate-500"
            data-testid="file-review-filter-search"
          />
        </div>
      </div>

      {data?.truncated && (
        <div className="text-xs text-amber-300" data-testid="file-review-register-truncated">
          Showing the newest {data.shown} of {data.total} reviews. Narrow the search to see the rest.
        </div>
      )}

      <div className="border border-slate-700/60 rounded-md overflow-x-auto">
        <table className="w-full text-xs text-slate-300" data-testid="file-review-register-table">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/60">
            <tr>
              <th className="text-left p-2">Matter</th>
              <th className="text-left p-2">Fee earner</th>
              <th className="text-left p-2">Reviewer</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Rating</th>
              <th className="text-left p-2">Actions open</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Reviewed</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr
                key={r.id}
                onClick={() => navigate(`/compliance/file-reviews/${r.id}`)}
                className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer"
                data-testid={`file-review-row-${r.id}`}
              >
                <td className="p-2">
                  <div className="text-slate-100">{r.matter_ref}</div>
                  <div className="text-slate-500">{r.client_name || ""}</div>
                </td>
                <td className="p-2 break-all">{r.fee_earner_email || "Not recorded"}</td>
                <td className="p-2 break-all">{r.reviewer_email || r.reviewer_name || "—"}</td>
                <td className="p-2">{labelOf(form?.review_types, r.file_review_type)}</td>
                <td className="p-2">{r.overall_rating ? labelOf(ratingOptions, r.overall_rating) : "Not recorded"}</td>
                <td className="p-2">{r.open_actions}</td>
                <td className="p-2">{r.historic ? "Closed (historic)" : STATUS_LABEL[r.status] || r.status}</td>
                <td className="p-2">{r.submitted_at || r.opened_at ? day(r.submitted_at || r.opened_at) : r.historic_date || "—"}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-slate-500" data-testid="file-review-register-empty">
                  No file reviews yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && (
          <div className="p-3 text-xs text-slate-400 flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        )}
      </div>
    </div>
  );
}
